import {
  FilterRoute,
  getAppliedFilters,
  getCustomerInvite,
  getWorkspace,
  ListFilterKey,
  Payment,
  PaymentsRequest,
  PaymentStatusEnum,
  PaymentStatusNames,
  useAppSelector,
  useGetPaymentFilterMutation,
  useLazyGetPaymentListQuery,
} from '@zonofi/zono-money-store';
import { camelCase, debounce, startCase } from 'lodash';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useUrlDateRange } from '@zonofi/seller-hub/hooks/use-url-date-range';
import { useCustomDateRange } from '@zonofi/seller-hub/hooks/use-custom-date-range';
import moment from 'moment';
import {
  isToday,
  isYesterday,
  usePaginatedList,
} from '@zonofi/zono-money-whatsapp-stack';
import { shallowEqual } from 'react-redux';

export const usePaymentScreen = () => {
  const inviteData = useAppSelector(getCustomerInvite);
  const { dateRangeFromUrl: appliedDateRange } = useUrlDateRange();
  const { applyNewDateRange } = useCustomDateRange();
  const [searchKey, setSearchKey] = useState('');
  const [debouncedSearchKey, setDebouncedSearchKey] = useState('');
  const [showSearch, setShowSearch] = useState(false);
  const [appliedStatusFilter, setAppliedStatusFilter] = useState<string>('');
  const [isSearching, setIsSearching] = useState(false);
  const [error, setError] = useState({
    show: false,
    code: null,
    message: null,
  });
  const [filterPage, setFilterPage] = useState<string>('');
  const [getPaymentsListAPI, getPaymentsListAPIResponse] =
    useLazyGetPaymentListQuery();
  const [getPaymentFilterApi, getPaymentFilterApiResponse] =
    useGetPaymentFilterMutation();
  const openFiltersDrawer = (filterPage: string) => {
    setFilterPage(filterPage);
  };
  const rootFilters = useAppSelector(
    getAppliedFilters(FilterRoute.Payment),
    shallowEqual
  );
  const memoizedRange = useMemo(
    () => ({
      startDate: appliedDateRange?.startDate,
      endDate: appliedDateRange?.endDate,
    }),
    [appliedDateRange?.startDate, appliedDateRange?.endDate]
  );
  const appliedFilters = useMemo(() => {
    const isAppliedDateToday = isToday(
      memoizedRange.startDate,
      memoizedRange.endDate
    );
    const isAppliedDateYesterday = isYesterday(
      memoizedRange.startDate,
      memoizedRange.endDate
    );

    const dateFilter = isAppliedDateToday
      ? 'Today'
      : isAppliedDateYesterday
        ? 'Yesterday'
        : `${moment(memoizedRange.startDate).format('DD-MM-YYYY')} to ${moment(memoizedRange.endDate).format('DD-MM-YYYY')}`;
    return [dateFilter];
  }, [memoizedRange]);
  useEffect(() => {
    getPaymentFilterApi({ sellerWorkspaceId: inviteData?.seller?.workspaceId });
  }, []);

  const debouncedFunction = useMemo(
    () =>
      debounce((value: string) => {
        setDebouncedSearchKey(value);
      }, 500),
    []
  );

  useEffect(() => {
    if (searchKey) {
      setIsSearching(true);
    }
    debouncedFunction(searchKey);
  }, [searchKey, debouncedFunction]);

  const statusFilters = useMemo(() => {
    const statusList = getPaymentFilterApiResponse?.data?.status ?? [];
    const filters = statusList.map((status) => ({
      id: PaymentStatusEnum[status as keyof typeof PaymentStatusEnum],
      value: startCase(camelCase(status)),
      selected: appliedStatusFilter === status,
    }));

    filters.unshift({
      id: PaymentStatusEnum.DEFAULT,
      value: 'All',
      selected: appliedStatusFilter === '',
    });

    return filters;
  }, [getPaymentFilterApiResponse?.data?.status, appliedStatusFilter]);
  const statusChips = useMemo(() => {
    return (
      statusFilters?.map(({ id, value }) => ({
        label: startCase(value),
        value: id,
        onClick: () => {
          setAppliedStatusFilter(id);
        },
      })) || []
    );
  }, [statusFilters]);
  const fetchPaymentList = useCallback(
    (params: { pageNo: number; pageSize: number }) => {
      const doctype = rootFilters.listFilter?.[ListFilterKey.PaymentType];
      const paymentMode = rootFilters.listFilter?.[ListFilterKey.PaymentMode];

      getPaymentsListAPI({
        endDate: `${moment(appliedDateRange?.endDate).format('YYYY-MM-DD')} 23:59:59`,
        startDate: `${moment(appliedDateRange?.startDate).format('YYYY-MM-DD')} 00:00:00`,
        sellerWorkspaceId: inviteData?.seller?.workspaceId,
        includeSettlement: true,
        ...(appliedStatusFilter ? { status: appliedStatusFilter } : {}),
        ...(doctype?.length ? { docType: doctype.join(',') } : {}),
        ...(paymentMode?.length ? { paymentMode: paymentMode.join(',') } : {}),
        ...(debouncedSearchKey ? { searchKey: debouncedSearchKey } : {}),
        ...params,
      });
    },
    [appliedDateRange, appliedStatusFilter, debouncedSearchKey, rootFilters]
  );

  const memoizedPaymentData = useMemo(() => {
    return getPaymentsListAPIResponse?.data?.payments ?? [];
  }, [getPaymentsListAPIResponse?.data?.payments]);

  const {
    data: paymentsData,
    pageNo,
    lastItemRef,
    isPageLoading,
    onScroll,
    isEndOfList,
  } = usePaginatedList<any>({
    fetchDataApiCall: fetchPaymentList,
    apiResponseData: memoizedPaymentData,
    isLoading: getPaymentsListAPIResponse.isLoading,
    uniqueKey: 'id',
    dependencies: [
      appliedDateRange?.startDate.toString(),
      appliedDateRange?.endDate.toString(),
      appliedStatusFilter,
      debouncedSearchKey,
      rootFilters,
    ],
    isError: getPaymentsListAPIResponse.isError,
    paginationMeta: {
      startRecord: getPaymentsListAPIResponse?.data?.startRecord || 0,
      endRecord: getPaymentsListAPIResponse?.data?.endRecord || 0,
      totalCount: getPaymentsListAPIResponse?.data?.total || 0,
    },
  });
  console.log(
    'paymentsData: ',
    paymentsData,
    'isEndOfList: ',
    isEndOfList,
    'pageNo: ',
    pageNo,
    'isLoading',
    pageNo === 1 &&
      (getPaymentsListAPIResponse?.isUninitialized ||
        getPaymentsListAPIResponse?.isFetching ||
        isSearching)
  );
  return {
    showSearch,
    setShowSearch,
    searchKey,
    setSearchKey,
    error,
    setError,
    pageNo,
    onScroll,
    lastItemRef,
    isPageLoading,
    statusChips,
    appliedStatusFilter,
    filterPage,
    setFilterPage,
    openFiltersDrawer,
    appliedDateRange,
    applyNewDateRange,
    appliedFilters,
    paymentsData,
    isLoading:
      pageNo === 1 &&
      (getPaymentsListAPIResponse?.isUninitialized ||
        getPaymentsListAPIResponse?.isFetching),
    getPaymentsListAPIResponse,
    isEndOfList,
  };
};
